import { getDialogueById } from '../../data/dialogues';
import { SCENE_KEYS } from '../../data/gameConstants';
import { getScoreRule } from '../../data/scoreRules';
import { NPC } from '../../objects/NPC';
import { SaveSystem } from '../../systems/SaveSystem';
import { ZoneCoreSystems } from '../../systems/ZoneCoreSystems';
import { AudioSystem } from '../../systems/AudioSystem';
import { DecisionPanel } from '../../ui/DecisionPanel';
import { ObjectiveHint } from '../../ui/ObjectiveHint';
import { SceneTransition } from '../../utils/SceneTransition';
import { Zone5EscortSystem } from './Zone5EscortSystem';
import { ZONE5_POSITIONS, Zone5Map } from './Zone5Map';

export class Zone5QuestFlow {
  private core: ZoneCoreSystems;
  private map: Zone5Map;
  private lansia: NPC;
  private ibuHamil: NPC;
  private ibundo: NPC;
  private hint: ObjectiveHint;
  private decision: DecisionPanel;
  private escort: Zone5EscortSystem;
  private escortStarted = false;
  private ibundoUnlocked = false;
  private transitioning = false;

  constructor(
    core: ZoneCoreSystems,
    map: Zone5Map,
    lansia: NPC,
    ibuHamil: NPC,
    ibundo: NPC,
    hint: ObjectiveHint,
    decision: DecisionPanel,
    escort: Zone5EscortSystem,
  ) {
    this.core = core;
    this.map = map;
    this.lansia = lansia;
    this.ibuHamil = ibuHamil;
    this.ibundo = ibundo;
    this.hint = hint;
    this.decision = decision;
    this.escort = escort;
  }

  start(): void {
    this.applyResumeState();

    if (this.core.quest.getStatus('zone5_go_safe_zone') !== 'completed') {
      this.core.quest.activateQuest('zone5_go_safe_zone');
      this.core.showIntroDialogue('zone5_narrator', () => this.onIntroComplete());
    }

    this.refreshQuestUI();
  }

  onVulnerableInteract(): void {
    if (this.escortStarted || this.core.quest.getStatus('zone5_escort_safe') === 'completed') return;
    if (this.core.quest.getStatus('zone5_panic_path') === 'completed') return;

    if (this.core.quest.getStatus('zone5_meet_vulnerable') !== 'completed') {
      this.startMeetConversation();
      return;
    }

    if (this.core.quest.getStatus('zone5_choose_action') !== 'completed') {
      this.showActionChoiceFlow();
    }
  }

  /** Percakapan warga rentan → panel pilihan bantu / panik */
  private showActionChoiceFlow(): void {
    if (this.core.quest.getStatus('zone5_choose_action') === 'completed') return;
    if (this.decision.isVisible() || this.core.dialogue.isActive()) return;

    const openPanel = () => this.showActionChoice();
    const help = getDialogueById('zone5_npc_help');
    if (help) {
      this.core.dialogue.show(help, openPanel);
    } else {
      openPanel();
    }
  }

  private startMeetConversation(): void {
    if (this.core.dialogue.isActive() || this.decision.isVisible()) return;

    const meet = getDialogueById('zone5_meet_vulnerable');
    const afterMeet = () => {
      this.core.quest.completeQuest('zone5_meet_vulnerable');
      this.core.quest.activateQuest('zone5_choose_action');
      this.refreshQuestUI();
      this.showActionChoiceFlow();
    };

    if (meet) {
      this.core.dialogue.show(meet, afterMeet);
    } else {
      afterMeet();
    }
  }

  onIbundoInteract(): void {
    if (!this.ibundoUnlocked) return;
    this.transitionToEnding();
  }

  prepareFrame(): void {
    if (this.decision.isVisible()) {
      this.core.getPlayer().setMovementEnabled(false);
    }
  }

  update(_playerX: number, _playerY: number, delta: number): void {
    if (this.decision.isVisible()) {
      this.core.getPlayer().setMovementEnabled(false);
      this.decision.handleInput(this.core.input);
    } else if (!this.escort.isActive()) {
      this.core.getPlayer().setMovementEnabled(true);
    }

    if (this.escort.isActive()) {
      const player = this.core.getPlayer();
      const arrived = this.escort.update(player, delta);
      if (arrived) this.onEscortComplete();
    }
  }

  private applyResumeState(): void {
    const goDone = this.core.quest.getStatus('zone5_go_safe_zone') === 'completed';
    const meetDone = this.core.quest.getStatus('zone5_meet_vulnerable') === 'completed';
    const chooseDone = this.core.quest.getStatus('zone5_choose_action') === 'completed';
    const escortDone = this.core.quest.getStatus('zone5_escort_safe') === 'completed';
    const panicDone = this.core.quest.getStatus('zone5_panic_path') === 'completed';

    this.ibundo.setInteractEnabled(false);

    if (escortDone || panicDone) {
      this.unlockIbundo();
      this.core.quest.activateQuest('zone5_meet_ibundo');
      this.hint.show('Temui Ibundo', ZONE5_POSITIONS.IBUNDO_HINT.x, ZONE5_POSITIONS.IBUNDO_HINT.y);
      return;
    }

    if (chooseDone) {
      this.resumeEscort();
      return;
    }

    if (meetDone) {
      this.setVulnerableInteractable(true);
      this.core.quest.activateQuest('zone5_choose_action');
      this.hint.show('Putuskan Tindakan', ZONE5_POSITIONS.VULNERABLE_QUEST_HINT.x, ZONE5_POSITIONS.VULNERABLE_QUEST_HINT.y);
      return;
    }

    if (goDone) {
      this.beginMeetStage();
      return;
    }

    this.setVulnerableInteractable(false);
  }

  private resumeEscort(): void {
    this.escortStarted = true;
    this.setVulnerableInteractable(false);
    this.core.quest.activateQuest('zone5_escort_safe');
    const player = this.core.getPlayer();
    this.escort.startEscort([this.lansia, this.ibuHamil], player.x, player.y);
    this.hint.show('Antar ke Titik Aman', ZONE5_POSITIONS.SAFE_ZONE.x, ZONE5_POSITIONS.SAFE_ZONE.y - 70);
  }

  private setVulnerableInteractable(enabled: boolean): void {
    this.lansia.setInteractEnabled(enabled);
    this.ibuHamil.setInteractEnabled(enabled);
  }

  private unlockIbundo(): void {
    this.ibundoUnlocked = true;
    this.map.openIbundoPath();
    this.core.removeObstacleNear(ZONE5_POSITIONS.IBUNDO_BLOCK.x, ZONE5_POSITIONS.IBUNDO_BLOCK.y);
    this.ibundo.setInteractEnabled(true);
  }

  /** Setelah intro: pemain jalan mendatangi warga rentan */
  private onIntroComplete(): void {
    this.core.quest.completeQuest('zone5_go_safe_zone');
    this.beginMeetStage();
    this.refreshQuestUI();
  }

  private beginMeetStage(): void {
    this.core.quest.activateQuest('zone5_meet_vulnerable');
    this.setVulnerableInteractable(true);
    this.hint.show(
      'Temui Warga Rentan',
      ZONE5_POSITIONS.VULNERABLE_QUEST_HINT.x,
      ZONE5_POSITIONS.VULNERABLE_QUEST_HINT.y,
    );
  }

  private showActionChoice(): void {
    if (this.decision.isVisible()) return;
    if (this.core.quest.getStatus('zone5_choose_action') === 'completed') return;

    this.setVulnerableInteractable(false);
    this.core.setMobileControlsVisible(false);
    this.decision.show(
      'Pilih Tindakan',
      'Apa yang akan kamu lakukan?',
      [
        { id: 'help', label: 'Berhenti sebentar dan bantu mereka menuju zona aman.' },
        { id: 'panic', label: 'Langsung lari mencari Ibundo karena panik.' },
      ],
      (id) => {
        this.core.setMobileControlsVisible(true);
        this.core.getPlayer().setMovementEnabled(true);
        this.handleActionChoice(id);
      },
    );
  }

  private handleActionChoice(choice: string): void {
    this.core.quest.completeQuest('zone5_choose_action');
    this.setVulnerableInteractable(false);
    this.hint.hide();
    this.refreshQuestUI();

    if (choice === 'panic') {
      AudioSystem.playSfx('ERROR');
      const rule = getScoreRule('zone5_panic');
      this.core.score.showScoreFeedback(rule ? rule.points : 0);

      const wrong = getDialogueById('zone5_wrong_action');
      const proceed = () => this.onPanicPath();
      if (wrong) {
        this.core.dialogue.show(wrong, proceed);
      } else {
        proceed();
      }
      return;
    }

    this.core.quest.activateQuest('zone5_escort_safe');
    this.escortStarted = true;
    this.refreshQuestUI();

    const success = getDialogueById('zone5_help_success');
    const startEscort = () => {
      const player = this.core.getPlayer();
      this.escort.startEscort([this.lansia, this.ibuHamil], player.x, player.y);
      this.hint.show('Antar ke Titik Aman', ZONE5_POSITIONS.SAFE_ZONE.x, ZONE5_POSITIONS.SAFE_ZONE.y - 70);
    };

    if (success) {
      this.core.dialogue.show(success, startEscort);
    } else {
      startEscort();
    }
  }

  private onPanicPath(): void {
    this.core.quest.completeQuest('zone5_panic_path');
    this.core.quest.activateQuest('zone5_meet_ibundo');
    this.unlockIbundo();
    this.hint.show('Temui Ibundo', ZONE5_POSITIONS.IBUNDO_HINT.x, ZONE5_POSITIONS.IBUNDO_HINT.y);
    this.refreshQuestUI();
    SaveSystem.save({ rafflesiaPoints: this.core.score.getScore() });
  }

  private onEscortComplete(): void {
    this.core.quest.completeQuest('zone5_escort_safe');
    this.core.quest.activateQuest('zone5_meet_ibundo');

    const rule = getScoreRule('zone5_help');
    if (rule) this.core.score.addScore(rule.points, rule.label);

    this.unlockIbundo();
    this.hint.show('Temui Ibundo', ZONE5_POSITIONS.IBUNDO_HINT.x, ZONE5_POSITIONS.IBUNDO_HINT.y);
    this.refreshQuestUI();
    SaveSystem.save({ rafflesiaPoints: this.core.score.getScore() });
  }

  private transitionToEnding(): void {
    if (this.transitioning) return;
    this.transitioning = true;
    this.core.quest.completeQuest('zone5_meet_ibundo');

    const save = SaveSystem.load();
    if (!save.missionBonusAwarded) {
      const rule = getScoreRule('ending_mission');
      if (rule) this.core.score.addScore(rule.points, rule.label);
    }

    SaveSystem.save({
      currentScene: SCENE_KEYS.ENDING,
      currentZone: 5,
      rafflesiaPoints: this.core.score.getScore(),
      missionBonusAwarded: true,
    });

    this.hint.hide();
    SceneTransition.fadeOutAndStart(this.core.scene, SCENE_KEYS.ENDING, 600);
  }

  private refreshQuestUI(): void {
    this.core.refreshQuestPanel(5);
  }

  destroy(): void {
    this.escort.destroy();
    this.decision.destroy();
    this.hint.destroy();
  }
}
